<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import type { Platform, Country } from '@/stores/admin'
import {
  platformAdvertiserCreateSchema,
  platformAdvertiserUpdateSchema,
} from '@/views/forms/schema'
import type { FormSubmitEvent } from '@nuxt/ui'
import type { z } from 'zod'
import { formatPlatformName, validatePattern } from '@/lib/utils'

// Grouped advertiser for edit mode
type GroupedAdvertiser = {
  platform_id: string
  platform_code: string
  platform_name: string
  platform_advertiser_identifier: string
  countries: { code: string | null; name: string | null }[]
  extra_kwargs: Record<string, unknown> | null
  search_type?: 'keyword' | 'advertiser_name'
}

const props = defineProps<{
  advertiser: GroupedAdvertiser | null
  platforms: Platform[]
  countries: Country[]
}>()

const emit = defineEmits<{
  submit: [
    data: {
      platform_id?: string
      platform_advertiser_identifier: string
      country_codes: (string | null)[]
      extra_kwargs?: Record<string, unknown> | null
      search_type?: 'keyword' | 'advertiser_name'
    },
  ]
  cancel: []
}>()

const isEditMode = computed(() => !!props.advertiser)

const state = ref({
  platform_id: props.advertiser?.platform_id || '',
  platform_advertiser_identifier: props.advertiser?.platform_advertiser_identifier || '',
})

// Country selection - multi for create, single for edit
const selectedCountryCodes = ref<string[]>([])

// TikTok-specific: relevant_adv_biz_ids and search_type
const tiktokBizIds = ref<string[]>([])
const newBizId = ref('')
const searchType = ref<'keyword' | 'advertiser_name'>('keyword')

// Snapchat-specific: relevant_profile_names
const snapchatProfileNames = ref<string[]>([])
const newProfileName = ref('')
const profileNameError = ref<string | null>(null)
// Snapchat null sentinel: when true, `null` is appended to relevant_profile_names
// on submit, signaling to gabellotto to also keep ads whose profile_name is
// exactly null or "" (used for brands like Chanel whose ads are tagged
// inconsistently).
const includeNullProfile = ref<boolean>(false)

watch(
  () => props.advertiser,
  (newAdvertiser) => {
    state.value = {
      platform_id: newAdvertiser?.platform_id || '',
      platform_advertiser_identifier: newAdvertiser?.platform_advertiser_identifier || '',
    }
    // In edit mode, set countries from grouped advertiser
    selectedCountryCodes.value = newAdvertiser?.countries
      ? newAdvertiser.countries.map((c) => c.code).filter((c): c is string => c !== null)
      : []
    // Load TikTok biz IDs from extra_kwargs
    const bizIds = newAdvertiser?.extra_kwargs?.relevant_adv_biz_ids
    tiktokBizIds.value = Array.isArray(bizIds)
      ? bizIds.filter((x): x is string => typeof x === 'string')
      : []
    // Load TikTok search type
    searchType.value = newAdvertiser?.search_type ?? 'keyword'
    // Load Snapchat profile names from extra_kwargs. null in the array is a
    // sentinel toggle, not a chip — extract it into includeNullProfile.
    const profileNames = newAdvertiser?.extra_kwargs?.relevant_profile_names
    if (Array.isArray(profileNames)) {
      snapchatProfileNames.value = profileNames.filter((x): x is string => typeof x === 'string')
      includeNullProfile.value = profileNames.includes(null)
    } else {
      snapchatProfileNames.value = []
      includeNullProfile.value = false
    }
  },
  { immediate: true },
)

// Root platforms only (no parent)
const rootPlatforms = computed(() => props.platforms.filter((p) => !p.parent_platform_id))

const platformOptions = computed(() =>
  rootPlatforms.value.map((p) => ({
    label: formatPlatformName(p.name),
    value: p.id,
  })),
)

// Check if selected platform is TikTok
const selectedPlatform = computed(() =>
  props.platforms.find((p) => p.id === state.value.platform_id),
)
const isTikTok = computed(() => selectedPlatform.value?.code === 'TIKTOK')
const isSnapchat = computed(() => selectedPlatform.value?.code === 'SNAPCHAT')

// Reset platform-specific chip state when the user switches platform in
// create mode. Without this, stale chips persist across a TikTok -> Snapchat
// -> TikTok toggle within a single form session.
watch(
  () => state.value.platform_id,
  () => {
    tiktokBizIds.value = []
    bizIdError.value = null
    newBizId.value = ''
    searchType.value = 'keyword'
    snapchatProfileNames.value = []
    profileNameError.value = null
    newProfileName.value = ''
    includeNullProfile.value = false
  },
)

// Advertiser ID validation based on platform regex pattern
const advertiserIdError = computed(() =>
  validatePattern(
    state.value.platform_advertiser_identifier,
    selectedPlatform.value?.advertiser_id_pattern ?? null,
    formatPlatformName(selectedPlatform.value?.name ?? ''),
  ),
)

// TikTok biz ID management
// ponytail: allow alphanumeric + common separators; tighten if TikTok spec is known
const tiktokBizIdPattern = /^[\w.-]+$/
const bizIdError = ref<string | null>(null)

function addBizId() {
  const id = newBizId.value.trim()
  if (!id) return

  if (!tiktokBizIdPattern.test(id)) {
    bizIdError.value = 'Business ID contains invalid characters'
    return
  }

  if (!tiktokBizIds.value.includes(id)) {
    tiktokBizIds.value.push(id)
    newBizId.value = ''
    bizIdError.value = null
  }
}

function removeBizId(id: string) {
  tiktokBizIds.value = tiktokBizIds.value.filter((i) => i !== id)
}

// Snapchat profile name management
function addProfileName() {
  const name = newProfileName.value.trim()
  if (!name) return

  // Case-insensitive dedupe, preserve entered casing
  const nameLower = name.toLowerCase()
  if (snapchatProfileNames.value.some((existing) => existing.toLowerCase() === nameLower)) {
    profileNameError.value = 'Profile name already added'
    return
  }

  snapchatProfileNames.value.push(name)
  newProfileName.value = ''
  profileNameError.value = null
}

function removeProfileName(name: string) {
  const nameLower = name.toLowerCase()
  snapchatProfileNames.value = snapchatProfileNames.value.filter(
    (i) => i.toLowerCase() !== nameLower,
  )
  profileNameError.value = null
}

// Country options - no Global option in multi-select, leave empty for global
// Include country code in label for searchability
const countryOptions = computed(() =>
  props.countries.map((c) => ({
    label: `${c.emoji} ${c.name_en} (${c.code})`,
    value: c.code,
  })),
)

const schema = computed(() =>
  isEditMode.value ? platformAdvertiserUpdateSchema : platformAdvertiserCreateSchema,
)

function onSubmit(
  event: FormSubmitEvent<
    z.output<typeof platformAdvertiserCreateSchema | typeof platformAdvertiserUpdateSchema>
  >,
) {
  // Build extra_kwargs based on platform
  let extra_kwargs: Record<string, unknown> | null = null
  if (isTikTok.value && tiktokBizIds.value.length > 0) {
    extra_kwargs = { relevant_adv_biz_ids: tiktokBizIds.value }
  } else if (
    isSnapchat.value &&
    (snapchatProfileNames.value.length > 0 || includeNullProfile.value)
  ) {
    const profiles: (string | null)[] = [...snapchatProfileNames.value]
    if (includeNullProfile.value) profiles.push(null)
    extra_kwargs = { relevant_profile_names: profiles }
  }

  // Determine country codes to submit
  const countryCodes = selectedCountryCodes.value.length > 0 ? selectedCountryCodes.value : [null]

  emit('submit', {
    platform_id: isEditMode.value ? undefined : state.value.platform_id,
    platform_advertiser_identifier: event.data.platform_advertiser_identifier,
    country_codes: countryCodes,
    extra_kwargs,
    search_type: isTikTok.value ? searchType.value : undefined,
  })
}

// TikTok advertiser_name requires exactly 1 biz_id
const advNameBizIdError = computed(() => {
  if (!isTikTok.value || searchType.value !== 'advertiser_name') return null
  if (tiktokBizIds.value.length !== 1) {
    return 'Advertiser Name search requires exactly 1 business ID'
  }
  return null
})

const isSubmitDisabled = computed(
  () =>
    !state.value.platform_advertiser_identifier.trim() ||
    (!isEditMode.value && !state.value.platform_id) ||
    !!advertiserIdError.value ||
    !!advNameBizIdError.value,
)
</script>

<template>
  <div class="p-4 space-y-6">
    <UForm :schema="schema" :state="state" class="space-y-4" @submit="onSubmit">
      <UFormField label="Platform" name="platform_id" :required="!isEditMode">
        <USelectMenu
          v-model="state.platform_id"
          :items="platformOptions"
          :disabled="isEditMode"
          placeholder="Select platform"
          value-key="value"
          class="w-full"
          :ui="{ content: '!max-h-[28rem]' }"
        />
        <template #hint v-if="isEditMode">
          <span class="text-xs text-muted">Platform cannot be changed</span>
        </template>
      </UFormField>

      <UFormField label="Advertiser ID" name="platform_advertiser_identifier" required>
        <UInput
          v-model="state.platform_advertiser_identifier"
          placeholder="Platform-specific advertiser ID"
          class="w-full font-mono"
          :color="advertiserIdError ? 'error' : undefined"
        />
        <template #hint>
          <span v-if="advertiserIdError" class="text-xs text-error">{{ advertiserIdError }}</span>
          <span v-else class="text-xs text-muted">{{
            selectedPlatform?.advertiser_id_hint || 'Platform-specific advertiser ID'
          }}</span>
        </template>
      </UFormField>

      <UFormField label="Countries" name="country_codes" hint="Leave empty for global mapping">
        <USelectMenu
          v-model="selectedCountryCodes"
          :items="countryOptions"
          multiple
          placeholder="Select countries (leave empty for global)"
          value-key="value"
          searchable
          :search-attributes="['label']"
          class="w-full"
        />
      </UFormField>

      <!-- TikTok-specific: Relevant Advertiser Business IDs -->
      <UFormField
        v-if="isTikTok"
        label="Relevant Advertiser Business IDs"
        name="relevant_adv_biz_ids"
        :hint="
          searchType === 'advertiser_name'
            ? 'Exactly 1 business ID required'
            : 'Optional business IDs'
        "
        :required="searchType === 'advertiser_name'"
      >
        <div class="space-y-2">
          <div class="flex gap-2">
            <UInput
              v-model="newBizId"
              placeholder="Enter business ID"
              class="flex-1 font-mono"
              :color="bizIdError ? 'error' : undefined"
              :disabled="searchType === 'advertiser_name' && tiktokBizIds.length >= 1"
              @keydown.enter.prevent="addBizId"
            />
            <UButton
              type="button"
              color="neutral"
              variant="soft"
              :disabled="searchType === 'advertiser_name' && tiktokBizIds.length >= 1"
              @click="addBizId"
            >
              Add
            </UButton>
          </div>
          <p v-if="bizIdError" class="text-xs text-error">{{ bizIdError }}</p>
          <p v-if="advNameBizIdError" class="text-xs text-error">{{ advNameBizIdError }}</p>
          <div v-if="tiktokBizIds.length > 0" class="flex flex-wrap gap-1">
            <UBadge
              v-for="id in tiktokBizIds"
              :key="id"
              color="neutral"
              variant="subtle"
              class="font-mono"
            >
              {{ id }}
              <UButton
                type="button"
                icon="i-lucide-x"
                color="neutral"
                variant="ghost"
                size="xs"
                class="ml-1 -mr-1"
                @click="removeBizId(id)"
              />
            </UBadge>
          </div>
        </div>
      </UFormField>

      <!-- TikTok-specific: Search Type toggle -->
      <UFormField
        v-if="isTikTok"
        label="Search Type"
        name="search_type"
        hint="How to search for ads in TikTok Ad Library"
      >
        <URadioGroup
          v-model="searchType"
          :items="[
            { label: 'Keyword', value: 'keyword', description: 'Search ads by keyword match' },
            {
              label: 'Advertiser Name',
              value: 'advertiser_name',
              description: 'Search ads by exact advertiser name',
            },
          ]"
        />
      </UFormField>

      <!-- Snapchat-specific: Relevant Profile Names -->
      <UFormField
        v-if="isSnapchat"
        label="Relevant Profile Names"
        name="relevant_profile_names"
        hint="Snapchat profile names to include"
      >
        <div class="space-y-2">
          <div class="flex gap-2">
            <UInput
              v-model="newProfileName"
              placeholder="Enter profile name"
              class="flex-1 font-mono"
              :color="profileNameError ? 'error' : undefined"
              @keydown.enter.prevent="addProfileName"
            />
            <UButton type="button" color="neutral" variant="soft" @click="addProfileName">
              Add
            </UButton>
          </div>
          <p v-if="profileNameError" class="text-xs text-error">{{ profileNameError }}</p>
          <div v-if="snapchatProfileNames.length > 0" class="flex flex-wrap gap-1">
            <UBadge
              v-for="name in snapchatProfileNames"
              :key="name"
              color="neutral"
              variant="subtle"
              class="font-mono"
            >
              {{ name }}
              <UButton
                type="button"
                icon="i-lucide-x"
                color="neutral"
                variant="ghost"
                size="xs"
                class="ml-1 -mr-1"
                @click="removeProfileName(name)"
              />
            </UBadge>
          </div>
          <UCheckbox
            v-model="includeNullProfile"
            label="Also include ads with no profile name"
            class="mt-2"
          />
        </div>
      </UFormField>

      <div class="flex justify-end gap-2 pt-4">
        <UButton type="button" color="neutral" variant="soft" @click="emit('cancel')">
          Cancel
        </UButton>
        <UButton type="submit" color="primary" :disabled="isSubmitDisabled">
          {{ isEditMode ? 'Update' : 'Add Advertiser' }}
        </UButton>
      </div>
    </UForm>
  </div>
</template>
